import type { IssueAssessmentHistoryResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetIssueAssessmentHistoryQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIssueAssessmentHistoryDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type IssueAssessmentHistoryArgs = {
  startDate?: string | null;
  endDate?: string | null;
  // V3 / tRPC leg: department + tag types are applied server-side (CUS-257).
  departmentTypeIds?: string[] | null;
  tagTypeIds?: string[] | null;
  // V2 / GraphQL leg: the audit `bool_exp` has no departments/tags relationship,
  // so the caller pre-resolves the selected departments + tags to the owning
  // issue Ids (see `useGetIssueIdsByDashboardFilter`) and we narrow by
  // `ParentIssueId`. `null` means "no relationship filter"; an empty array means
  // "no matching issues" and must still filter to zero rows.
  parentIssueIds?: string[] | null;
};

export const useGetIssueAssessmentHistory = createQueryHook<
  IssueAssessmentHistoryArgs,
  IssueAssessmentHistoryResponseRow[],
  GetIssueAssessmentHistoryQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.issueAssessment.getHistory.queryOptions({
      startDate: args.startDate ?? null,
      endDate: args.endDate ?? null,
      departmentTypeIds: args.departmentTypeIds ?? null,
      tagTypeIds: args.tagTypeIds ?? null,
    }),
  mapTrpcDataToGraphQL: (data) => ({
    issue_assessment_audit: data,
  }),
  graphqlDocument: GetIssueAssessmentHistoryDocument,
  // The audit `bool_exp` has no departments relationship, so the GraphQL leg
  // narrows by `ParentIssueId` using the issue Ids the caller pre-resolved from
  // the selected departments (CUS-257). `parentIssueIds == null` = no department
  // filter; `[]` = filter to zero rows (departments selected, no issues match).
  graphqlVariables: (args) => {
    const hasRange = !!args.startDate && !!args.endDate;

    return {
      where: {
        Type: {
          _eq: Parent_Type_Enum.IssueAssessment,
        },
        ...(args.parentIssueIds != null && {
          ParentIssueId: { _in: args.parentIssueIds },
        }),
        ...(hasRange && {
          _or: [
            { ActualCloseDate: { _gte: args.startDate } },
            { CreatedAtTimestamp: { _lte: args.endDate } },
          ],
        }),
      },
    };
  },
});
